// Quantity bundle picker ("Bundle & save").
// Each option is a radio with data-quantity. Choosing one updates the cart right away:
//   - if the selected variant is already in the cart, its line quantity is set to the tier quantity;
//   - otherwise the tier quantity is added.
// It also mirrors the quantity into the form's quantity input, so Add to cart / Buy it now match.
// Cart writes go through Shopify.actions.updateCart (the same path as Add to cart), so the cart
// drawer and count refresh, then the drawer opens as confirmation.
// Buy 1 is highlighted by default. On load (and after a variant change) the pill matching the variant's current
// cart quantity (1-3) is highlighted instead. Tapping any pill, even the highlighted one, updates the cart.
class BundlePicker extends HTMLElement {
  #controller = null;
  #request = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.form = this.closest('form');
    this.quantityInput = this.form?.querySelector('input[name="quantity"]');

    this.addEventListener(
      'click', // Radio clicks fire for taps, label taps and arrow keys, including re-tapping the selected pill.
      (event) => {
        const radio = event.target.closest('input[type="radio"][data-quantity]');
        if (!radio || !radio.checked) return;
        const quantity = Number(radio.dataset.quantity);
        this.#mirrorQuantity(quantity);
        this.#applyToCart(quantity);
      },
      { signal }
    );

    this.#syncFromCart();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#request?.abort();
  }

  get #variantId() {
    const input = this.form?.elements.namedItem('id');
    return input instanceof HTMLInputElement ? String(input.value).trim() : '';
  }

  get #root() {
    return window.Shopify?.routes?.root || '/';
  }

  #mirrorQuantity(quantity) {
    if (!this.quantityInput) return;
    this.quantityInput.value = String(quantity);
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  async #fetchCart(signal) {
    const response = await fetch(`${this.#root}cart.js`, { headers: { Accept: 'application/json' }, signal });
    if (!response.ok) throw new Error('cart fetch failed');
    return response.json();
  }

  async #syncFromCart() {
    const variantId = this.#variantId;
    if (!variantId) return;
    try {
      const cart = await this.#fetchCart(this.#controller.signal);
      const total = cart.items
        .filter((item) => String(item.variant_id) === variantId)
        .reduce((sum, item) => sum + item.quantity, 0);
      const match = this.querySelector(`input[type="radio"][data-quantity="${total}"]`);
      if (match) {
        match.checked = true; // Otherwise keep the default (Buy 1) highlighted.
        this.#mirrorQuantity(total);
      }
    } catch {
      /* Keep the default highlight if the cart can't be read. */
    }
  }

  #showError(message) {
    const error = this.form?.querySelector('[data-add-to-cart-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  async #applyToCart(quantity) {
    const variantId = this.#variantId;
    if (!variantId || !window.Shopify?.actions?.updateCart) return;

    this.#request?.abort();
    this.#request = new AbortController();
    const { signal } = this.#request;
    this.setAttribute('aria-busy', 'true');
    this.#showError('');

    try {
      const cart = await this.#fetchCart(signal);
      const existing = cart.items.filter((item) => String(item.variant_id) === variantId);

      let lines;
      if (existing.length) {
        // Set the first matching line to the tier quantity and clear any duplicates of the same variant.
        lines = existing.map((item, index) => ({ id: item.key, quantity: index === 0 ? quantity : 0 }));
      } else {
        lines = [{ merchandiseId: variantId, quantity }];
      }

      const result = await window.Shopify.actions.updateCart({ lines }, { event: { context: 'product' }, signal });
      if (signal.aborted) return;

      const message = result?.userErrors?.[0]?.message;
      if (message) {
        this.#showError(message);
        return;
      }
      await window.Shopify.actions.openCart?.();
    } catch (error) {
      if (signal.aborted) return;
      this.#showError(this.closest('[data-error-message]')?.dataset.errorMessage || String(error?.message || ''));
    } finally {
      if (!signal.aborted) this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('s-bundle-picker')) customElements.define('s-bundle-picker', BundlePicker);
